import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, OnDestroy } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { SidebarService } from '../../../../core/services/utils/sidebar.service';
import { ProfessorAssignment } from '../../../../core/interfaces/professor';
import { ThemeService } from '../../../../core/services/utils/theme.service';
import { AuthService } from '../../../../core/services/utils/auth.service';
import { LanguageService } from '../../../../core/services/utils/language.service';
import { AgeTierService, AgeTier } from '../../../../core/services/utils/age-tier.service';
import { NotificationService } from '../../../../core/services/notifications/notification.service';
import { NotificationHubService } from '../../../../core/services/notifications/notification-hub.service';
import { NotificationDto } from '../../../../core/interfaces/notification';

import { LocalizedDatePipe } from '../../../../shared/pipes/localized-date.pipe';
@Component({
  selector: 'app-header',
  standalone: true,
  imports: [TranslateModule, CommonModule, LocalizedDatePipe],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css',
})
export class HeaderComponent implements OnInit, OnDestroy {
  isDark: boolean = false;
  currentTime: string = '';
  currentDate: string = '';
  currentDay: string = '';
  currentLang: string = 'ar';
  isLTR: boolean = false;
  isRTL: boolean = false;

  showLanguageDropdown: boolean = false;
  showUserDropdown: boolean = false;
  showNotifDropdown: boolean = false;
  isMobileSidebarOpen = false;
  isAdmin: boolean = false;
  isStudent: boolean = false;
  isTeacher: boolean = false;
  user: any;
  assignments: ProfessorAssignment[] = [];

  notifications: NotificationDto[] = [];
  unreadCount: number = 0;

  private clockInterval?: number;
  private assignmentsSubscription?: Subscription;
  private notifSub?: Subscription;
  private unreadSub?: Subscription;

  // RTL languages list
  private readonly RTL_LANGUAGES = ['ar', 'ar-TN', 'ar-DZ', 'he', 'fa', 'ur'];

  constructor(
    private themeService: ThemeService,
    private sidebarService: SidebarService,
    private authService: AuthService,
    private languageService: LanguageService,
    public ageTierService: AgeTierService,
    public notificationService: NotificationService,
    private notificationHub: NotificationHubService,
  ) {}

  // ─── Age tier (primaire / college / lycee) ───
  setTier(tier: AgeTier): void {
    this.ageTierService.setTier(tier);
  }

  tierLabelKey(tier: AgeTier): string {
    switch (tier) {
      case 'primaire': return 'AGE_TIER.PRIMAIRE';
      case 'college':  return 'AGE_TIER.COLLEGE';
      case 'lycee':    return 'AGE_TIER.LYCEE';
    }
  }

  tierIcon(tier: AgeTier): string {
    switch (tier) {
      case 'primaire': return 'fa-solid fa-puzzle-piece';
      case 'college':  return 'fa-solid fa-rocket';
      case 'lycee':    return 'fa-solid fa-graduation-cap';
    }
  }

  ngOnInit(): void {
    this.initHeader();
  }

  ngOnDestroy(): void {
    if (this.clockInterval) {
      clearInterval(this.clockInterval);
    }
    this.assignmentsSubscription?.unsubscribe();
    this.notifSub?.unsubscribe();
    this.unreadSub?.unsubscribe();
  }

  private async initHeader(): Promise<void> {
    this.isDark = this.themeService.isDarkMode();

    this.user = await this.authService.getUser();
    this.isTeacher = this.authService.hasRole('professor');

    this.assignmentsSubscription = this.authService.assignments$.subscribe(
      (data) => {
        this.assignments = data;
      },
    );

    if (this.isTeacher) {
      await this.authService.loadAssignments();
    }

    // Notifications
    this.notifSub = this.notificationService.notifications$.subscribe(
      (n) => (this.notifications = n),
    );
    this.unreadSub = this.notificationService.unreadCount$.subscribe(
      (c) => (this.unreadCount = c),
    );
    this.notificationService.list().subscribe();
    try {
      const token = await this.authService.getToken();
      if (token) {
        this.notificationHub.connect(() => token);
      }
    } catch {
      // SignalR is optional — fallback to REST polling only
    }

    this.languageService.loadInitialLanguage();
    this.currentLang = this.languageService.getCurrentLang();
    this.isLTR = this.languageService.isLTR();
    this.updateRTLStatus();

    this.updateClock();
    this.clockInterval = window.setInterval(() => this.updateClock(), 1000);
  }

  toggleNotifDropdown(event: MouseEvent): void {
    event.stopPropagation();
    this.showNotifDropdown = !this.showNotifDropdown;
    this.showLanguageDropdown = false;
    this.showUserDropdown = false;
  }

  markNotifAsRead(notif: NotificationDto, event: MouseEvent): void {
    event.stopPropagation();
    if (!notif.isRead) {
      this.notificationService.markAsRead(notif.id).subscribe();
    }
  }

  markAllNotifsAsRead(event: MouseEvent): void {
    event.stopPropagation();
    this.notificationService.markAllAsRead().subscribe();
  }

  // Toggle Theme
  toggleTheme(): void {
    this.themeService.toggleTheme();
    this.isDark = this.themeService.isDarkMode();
  }

  // Change Language
  changeLanguage(lang: string): void {
    this.languageService.setLanguage(lang);
    this.currentLang = lang;
    this.isLTR = this.languageService.isLTR();
    this.updateRTLStatus();
    this.showLanguageDropdown = false;
    this.updateClock(); // To localize time & date
  }

  // Update RTL status based on current language
  private updateRTLStatus(): void {
    this.isRTL = this.RTL_LANGUAGES.some((lang) =>
      this.currentLang.startsWith(lang),
    );
  }

  // Get locale string based on current language
  private getLocale(): string {
    switch (this.currentLang) {
      case 'ar':
        return 'ar-TN'; // Tunisian Arabic
      case 'fr':
        return 'fr-FR'; // French (France)
      case 'en':
        return 'en-US'; // English (US)
      default:
        return this.currentLang;
    }
  }

  // Handling Clock
  private updateClock(): void {
    const now = new Date();
    const locale = this.getLocale();

    // Format time (24-hour format for FR and AR, 12-hour for EN)
    this.currentTime = now.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: this.currentLang === 'en', // 12-hour only for English
    });

    // Format date
    this.currentDate = now.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    // Format day name
    this.currentDay = now.toLocaleDateString(locale, {
      weekday: 'long',
    });
  }

  // Handling Language Dropdown
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown') && !target.closest('.notif-dropdown')) {
      this.showLanguageDropdown = false;
      this.showUserDropdown = false;
      this.showNotifDropdown = false;
    }
  }

  // Toggle SideBar
  toggleSidebar(): void {
    this.sidebarService.toggleSidebar();
  }

  // LOGOUT
  logout(): void {
    this.authService.logout();
  }
}
